import React from "react";
import { Building2, Flame, LayoutDashboard, Snowflake, SunMedium, UserPlus, Users } from "lucide-react";

import { useAuthStore } from "../store/authStore";
import { getLeadsByClient, getLeadsByProperty, getDailyLeadsTrend, getLeadScoreDistribution, getWeeklyLeadsTrend, getMonthlyLeadsTrend } from "../hooks/useDashboard.js";

import { useDashboardSummary } from "../queries/auth/useDashboard.js";

const LeadsTrendChart = React.lazy(() =>
  import("../components/dashboard/LeadsTrendChart")
);

const DashboardCard = React.lazy(() =>
  import("../components/dashboard/DashboardCard")
);

const LeadScoreChart = React.lazy(() =>
  import("../components/dashboard/LeadScoreChart")
);

const PropertyLeadsChart = React.lazy(() =>
  import("../components/dashboard/PropertyLeadsChart")
);

export default function Dashboard() {
  const { role } = useAuthStore();
  const isAdmin = role === "admin";

  const { data: dashboardSummary } = useDashboardSummary();
  const stats = dashboardSummary?.data?.summary || {};
  let leadsByClient = [];
  if (isAdmin) {
    leadsByClient = getLeadsByClient(dashboardSummary?.data?.leads_by_client || []);
  }
  const leadsByProperty = getLeadsByProperty(dashboardSummary?.data?.leads_by_property || []);
  const leadScoreData = getLeadScoreDistribution(dashboardSummary?.data?.lead_score_distribution || []);
  const weeklyTrend = getWeeklyLeadsTrend(dashboardSummary?.data?.lead_trend?.weekly || []);
  const monthlyTrend = getMonthlyLeadsTrend(dashboardSummary?.data?.lead_trend?.monthly || []);
  const DailyTrend = getDailyLeadsTrend(dashboardSummary?.data?.lead_trend?.daily || []);

  return (
    <React.Suspense fallback={null}>
      <section className="dashboard-container">
        <div>
          <header className={`dashboard-header ${isAdmin ? "dashboard-header--admin" : "dashboard-header--agent"}`}>
            <div className="dashboard-header-icon" aria-hidden="true">
              <LayoutDashboard size={22} />
            </div>
            <div className="dashboard-header-copy">
              <div className="dashboard-header-title-row">
                <h1 className="dashboard-title">
                  {isAdmin
                    ? "LeadIQ Admin Dashboard"
                    : "LeadIQ Agent Dashboard"}
                </h1>
                <span className="dashboard-role-badge">
                  {isAdmin ? "Admin" : "Agent"}
                </span>
              </div>
              <p className="dashboard-subtitle">
                {isAdmin
                  ? "Portfolio overview across clients, properties, and leads"
                  : "Your properties and lead pipeline at a glance"}
              </p>
            </div>
          </header>

          {/* KPI Cards */}
          <p className="dashboard-section-label">Overview</p>
          <div className="dashboard-grid grid-cards mb-4">

            {isAdmin && (
              <DashboardCard
                title="Total Clients"
                value={stats.total_clients}
                variant="clients"
                icon={<Users size={20} />}
                to="/agents"
              />
            )}

            <DashboardCard
              title="Total Properties"
              value={stats.total_properties}
              variant="properties"
              icon={<Building2 size={20} />}
              to="/properties"
            />

            <DashboardCard
              title="Total Leads"
              value={stats.total_leads}
              variant="leads"
              icon={<UserPlus size={20} />}
              to="/leads"
            />

            <DashboardCard
              title="Hot Leads"
              value={stats.hot_leads}
              variant="hot"
              icon={<Flame size={20} />}
              to={Number(stats.hot_leads) > 0 ? "/leads?category=Hot" : ""}
            />

            <DashboardCard
              title="Warm Leads"
              value={stats.warm_leads}
              variant="warm"
              icon={<SunMedium size={20} />}
              to={Number(stats.warm_leads) > 0 ? "/leads?category=Warm" : ""}
            />

            <DashboardCard
              title="Cold Leads"
              value={stats.cold_leads}
              variant="cold"
              icon={<Snowflake size={20} />}
              to={Number(stats.cold_leads) > 0 ? "/leads?category=Cold" : ""}
            />
          </div>

          {/* Charts */}
          <p className="dashboard-section-label">Analytics</p>
          <div className="dashboard-charts mb-4">

            <div className="grid-auto-fit my-3">

              {/* Admin Only */}
              {isAdmin && (
                <div className="chart-box">
                  <PropertyLeadsChart
                    data={leadsByClient}
                    title="Leads by Client"
                    variant="client"
                  />
                </div>
              )}

              <div className="chart-box">
                <PropertyLeadsChart
                  data={leadsByProperty}
                  title="Leads by Property"
                  variant="property"
                />
              </div>
            </div>

            <div className="grid-auto-fit mt-4">
              <div className="chart-box">
                <LeadScoreChart data={leadScoreData} />
              </div>

              <div className="chart-box">
                <LeadsTrendChart
                  weeklyData={weeklyTrend}
                  monthlyData={monthlyTrend}
                  dailyData={DailyTrend}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </React.Suspense>
  );
}
